import useFlags from 'launchdarkly-react-client-sdk/lib/useFlags';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { boolean, mixed, string } from 'yup';
import { maxTacticTitleLength, maxTacticTitleLengthWithoutSuffixInTitle } from './TacticForm.constants';
import { mayaCepTemplateCreateRoute, mayaCepTemplateEditRoute } from 'basics/constants/routes.constants';
import { BiogenFunction, Channel, EngagementMetric, ReachMetric, TacticStatus } from 'basics/enums/maya.enums';
import { TimeZone } from 'basics/enums/timeZoneList.enums';
import { TestContextType } from 'basics/types/common.types';
import {
  getUTCDate,
  testDateRangeInValidationRange,
  testEndDayAfterStartDay,
  testEndDayNotBeforeStartDay,
  testStartDateIsSet,
} from 'basics/utils/dates/dates.utils';
import { isProduction } from 'config/config';
import { CtaType, CustomerEngagementPlanning, Tactic } from 'generated/maya.types';
import { DateRange } from 'yoda-ui/components/Form/DateRangePicker';
import { YodaFieldsState } from 'yoda-ui/yodaForm/yodaForm.types';

const IVADocumentIdRegex = isProduction ? /^[0-9]{6}$/ : /^[0-9]{3,6}$/;

const useTacticFormSchema = (
  cep: Partial<CustomerEngagementPlanning> | null | undefined,
  tactic: Partial<Tactic> | null | undefined,
  selectedChannel: Channel | null,
) => {
  const flags = useFlags();
  const { t } = useTranslation();
  const tacticTitle = tactic?.title || '';
  const isApprovedTactic = tactic?.status === TacticStatus.APPROVED;
  const isTacticWithoutSuffix = tacticTitle.length > maxTacticTitleLengthWithoutSuffixInTitle;
  const isOldTacticWithoutTarget = !flags.mayaBdcfcsd1251ExtendTacticEditingOfApprovedCep && isApprovedTactic && isTacticWithoutSuffix;
  const maxTitleLength = isOldTacticWithoutTarget ? maxTacticTitleLength : maxTacticTitleLengthWithoutSuffixInTitle;
  const maxLengthTitleMessage = `${t('maya_tactic_step_configuration_title_error_max_char', { count: maxTitleLength })}`;
  const requiredLabel = t('form_errors_required');

  const location = useLocation();
  const currentURL = location.pathname;
  const templateRoute = [mayaCepTemplateCreateRoute, mayaCepTemplateEditRoute];
  const isTemplate = templateRoute.includes(currentURL);

  const isKeyMessageMandatory = (currentChannel: Channel) => {
    return ([
      Channel.f2fMeeting,
      Channel.oneToOne,
      Channel.oneToOneKeyMessage,
      Channel.telephone,
      Channel.virtualMeeting,
    ].includes(currentChannel))
  && (!isTemplate);
  };

  const parentCepStartDate = cep?.dateStart ? new Date(cep?.dateStart) : null;
  const parentCepEndDate = cep?.dateEnd ? new Date(cep?.dateEnd) : null;

  return {
    channel: mixed<Channel | string>()
      .oneOf([...Object.values(Channel)])
      .required(),
    title: string()
      .required(requiredLabel)
      .max(maxTitleLength, maxLengthTitleMessage),
    date: mixed()
      .test({
        name: 'startDate is set',
        message: requiredLabel,
        test: testStartDateIsSet,
      })
      .test({
        name: 'endDate must be after startDate',
        message: t('timeSlotBooking_endDate_errorMessage_earlierThanStart'),
        test: testEndDayAfterStartDay,
      })
      .test({
        name: 'start date before end date',
        message: t('timeSlotBooking_endDate_errorMessage_earlierThanStart'),
        test: testEndDayNotBeforeStartDay,
      })
      .test({
        name: 'date in CEP date range',
        message: 'tactic date range must be in Parent cep date range',
        test: (dateRange, context) => {
          const [tacticDateStart, tacticDateEnd] = dateRange;
          const tacticDateStartUTC = tacticDateStart
            ? getUTCDate(tacticDateStart, `${(tacticDateStart as Date).getHours()}:${(tacticDateStart as Date).getMinutes()}`, TimeZone.UTC)
            : null;
          const tacticDateEndUTC = tacticDateEnd
            ? getUTCDate(tacticDateEnd, `${(tacticDateEnd as Date).getHours()}:${(tacticDateEnd as Date).getMinutes()}`, TimeZone.UTC)
            : null;

          const dateRangeUTC = [
            tacticDateStartUTC,
            tacticDateEndUTC,
          ] as DateRange;

          if (!testDateRangeInValidationRange(dateRangeUTC, [parentCepStartDate, parentCepEndDate])) {
            return context.createError(
              {
                message: t(
                  'maya_tactic_date_range_validation',
                  { startDate: parentCepStartDate?.toLocaleDateString(), endDate: parentCepEndDate?.toLocaleDateString() },
                ),
              },
            );
          }

          return true;
        },
      }),
    productionOwner: mixed<BiogenFunction | string>()
      .oneOf([...Object.values(BiogenFunction)], requiredLabel),
    executingTeam: mixed<BiogenFunction | string>()
      .oneOf([...Object.values(BiogenFunction)], requiredLabel)
      .required(),
    callToAction: mixed<CtaType | string>()
      .oneOf([...Object.keys(CtaType)]),
    reachMetric: mixed<ReachMetric | string>()
      .oneOf([...Object.values(ReachMetric), '']),
    engagementMetric: mixed<EngagementMetric | string>()
      .oneOf([...Object.values(EngagementMetric), '']),
    mandatory:
        flags.mayaAkma777EnableTacticGroupingImprovements
          ? boolean().strip()
          : boolean().required(),
    includeConsent: boolean(),
    assetName: string().max(254, t('maya_asset_name_field_max_length_validation')),
    keyMessage: string()
      .test({
        name: 'date in CEP date range',
        test: (value, context) => {
          const fieldsState: YodaFieldsState = (context.options as TestContextType)?.fieldsState;
          const channel = fieldsState.channel.value;

          if (isKeyMessageMandatory(channel) && !value) {
            return context.createError(
              {
                message: requiredLabel,
              },
            );
          }

          return true;
        },
      }),
    approvedDocumentId: string(),
    isKeyPerformanceIndicator: boolean(),
    IVADocumentId: selectedChannel === Channel.oneToOneIVA && !isTemplate ? (string()
      .test({
        name: 'IVA document id in tactic',
        message: t('maya_tactics_document_id_error'),
        test: (value) => {
          return !value || value.match(IVADocumentIdRegex) !== null;
        },
      })
      .required(requiredLabel)) : (string()
      .test({
        name: 'IVA document id in tactic',
        message: isProduction ? t('maya_tactics_document_id_error') : t('maya_tactics_document_id_dev_error'),
        test: (value) => {
          return !value || value.match(IVADocumentIdRegex) !== null;
        },
      })
      .notRequired()),
    dropMaterials: selectedChannel === Channel.oneToOneDropMaterial && !isTemplate
      ? string().required(requiredLabel)
      : string().notRequired(),
    eventId: string()
      .test({
        name: 'event id format not correct',
        message: t('maya_tactics_event_id_error'),
        test: (value) => {
          return !value || value.match(/^\d{4,8}$/) !== null;
        },
      })
      .notRequired(),
    actionOrder: string()
      .matches(/^$|^([1-9][0-9]?)$/, t('maya_tactics_action_order_error'))
      .required(requiredLabel),
    destinationUrl: string()
      .optional()
      .url(t('maya_tactics_destination_url_error'))
      .test({
        name: 'validDestinationUrl',
        message: t('maya_tactics_destination_url_error'),
        test: (value) => {
          if (!value) {
            return true;
          }

          const destinationUrl = new URL(value);

          return destinationUrl.pathname !== '/';
        },
      })
      .optional(),
    comment: string().max(500, t('maya_tactic_form_comment_max_length_error', { count: 500 })).notRequired(),
  };
};

export default useTacticFormSchema;
